import { useTranslation } from 'react-i18next'
import { Globe, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  languageConfig,
  supportedLanguages,
  type SupportedLanguage,
} from '@/i18n'

// Flag emoji mapping for visual appeal
const flagEmojis: Record<SupportedLanguage, string> = {
  de: '🇩🇪',
  en: '🇬🇧',
  es: '🇪🇸',
  ar: '🇸🇦',
}

export function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const currentLang = (i18n.language.substring(0, 2) ||
    'de') as SupportedLanguage
  const currentConfig = languageConfig[currentLang]

  const handleLanguageChange = (lang: SupportedLanguage) => {
    void i18n.changeLanguage(lang)
    // Update document direction for RTL support
    document.documentElement.dir = languageConfig[lang].dir
    document.documentElement.lang = lang
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-1 px-2 sm:gap-2 sm:px-3">
          <Globe className="h-4 w-4" />
          <span className="hidden sm:inline text-base">
            {flagEmojis[currentLang]}
          </span>
          <span className="hidden md:inline text-xs text-muted-foreground">
            {currentConfig.flag}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {supportedLanguages.map((lang) => {
          const config = languageConfig[lang]
          const isActive = lang === currentLang

          return (
            <DropdownMenuItem
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              className={`cursor-pointer ${isActive ? 'bg-accent' : ''}`}
            >
              <span className="text-base mr-2">{flagEmojis[lang]}</span>
              <span className="flex-1">{config.nativeName}</span>
              {isActive && <Check className="h-4 w-4 text-primary" />}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
